import './AgendaDetail.scss'

import {BlockType, Blocks} from '../blocks/Blocks'
import {
	Contentside,
	ContentsideContent,
	ContentsideSidebar
} from '../layout/Contentside'
import {Hero, HeroSide, HeroType} from '../layout/Hero'
import {PageData, WysiwygType} from '../../types'
import {SidebarBlockType, SidebarBlocks} from '../sidebar/SidebarBlocks'

import {Breadcrumbs} from '../layout/Breadcrumbs'
import {Icon} from '../ui/Icon'
import {Layout} from '../layout/Layout'
import React from 'react'
import {Theme} from '../Theme'
import format from 'date-fns/format'
import isSameDay from 'date-fns/isSameDay'
import parseISO from 'date-fns/parseISO'
import {style} from '../../util/style'
import {useTranslation} from '../../util/i18n'
import {wrap} from '@codeurs/front'

const styles = style('agendadetail', ['info'])

export type AgendaDetailType = {
	hero: HeroType
	contentblocks?: Array<BlockType>
	sidebarblocks?: Array<SidebarBlockType>
	blocks?: Array<BlockType>
}

type AgendaDetailInfoType = {
	information_title?: string
	information_text?: WysiwygType
	date: {
		start?: string
		end?: string
	}
	location: {
		no_address?: boolean
		street?: string
		housenumber?: string
		zip?: string
		city?: string
	}
}

export const AgendaDetail: React.FC<PageData & AgendaDetailType> = wrap(Layout)(
	({...props}) => {
		const {
			parents,
			title,
			hero,
			information_title,
			information_text,
			date,
			location,
			contentblocks,
			sidebarblocks,
			blocks
		} = props

		return (
			<div className={styles()}>
				{hero?.image && (
					<Hero {...hero} title={(hero && hero.title) || title} />
				)}
				<Breadcrumbs parents={parents} title={title} />
				<Contentside>
					<ContentsideContent>
						{(!hero || !hero?.image) && (
							<HeroSide {...hero} title={hero?.title || title} />
						)}
						<Blocks blocks={contentblocks} />
					</ContentsideContent>
					<ContentsideSidebar>
						<AgendaDetailInfo
							information_title={information_title}
							information_text={information_text}
							date={date}
							location={location}
						/>
						<SidebarBlocks blocks={sidebarblocks} />
					</ContentsideSidebar>
				</Contentside>
				<Blocks blocks={blocks} container={true} />
			</div>
		)
	}
)

const AgendaDetailInfo: React.FC<AgendaDetailInfoType> = ({
	information_title,
	information_text,
	date,
	location
}) => {
	const t = useTranslation('agenda')
	const {street, housenumber, zip, city} = location
	return (
		<div className={styles.info()}>
			<Theme.H3
				className={styles.info('title')}
				mod={['white', 'marked', 'slantleft']}
			>
				{information_title || t.information.title}
			</Theme.H3>
			{date && (
				<AgendaDetailInfoDate
					icon="calendar"
					start={date.start}
					end={date.end}
				/>
			)}
			{street && housenumber && zip && city && (
				<AgendaDetailInfoItem
					icon="pin"
					label={`${street} ${housenumber}\n${zip} ${city}`}
				/>
			)}
			{information_text && (
				<div className={styles.info('text')}>
					<Theme.Wysiwyg>{information_text}</Theme.Wysiwyg>
				</div>
			)}
		</div>
	)
}

const AgendaDetailInfoItem: React.FC<{
	icon?: 'pin'
	label?: string
}> = ({icon, label}) =>
	label && (
		<div className={styles.info('item')}>
			<span className={styles.info('item-icon')}>
				<Icon icon={icon} />
			</span>
			<p className={styles.info('item-label')}>{label}</p>
		</div>
	)

const AgendaDetailInfoDate: React.FC<{
	icon?: 'calendar'
	start?: string
	end?: string
}> = ({end, icon, start}) => {
	const t = useTranslation('agenda').information

	const parsedStartDate = start && parseISO(start)
	const startDate = parsedStartDate && format(parsedStartDate, 'dd/MM')
	const hours = parsedStartDate && format(parsedStartDate, 'HH:mm')
	if (!startDate && !hours) return null

	const parsedEndDate = end && parseISO(end)
	const endDate = parsedEndDate && format(parsedEndDate, 'dd/MM')
	const endHours = parsedEndDate && format(parsedEndDate, 'HH:mm')

	const daysAreTheSame = isSameDay(parsedStartDate, parsedEndDate)

	let data: JSX.Element = <></>

	if (endDate || endHours) {
		data = (
			<>
				{startDate && (
					<p className={styles.info('item-column-label')}>
						{endDate && endHours && daysAreTheSame
							? `${t.on} ${startDate}`
							: `${t.from} ${startDate}`}
						{hours && endHours && daysAreTheSame
							? ` ${t.from} ${hours}`
							: ` ${t.at} ${hours}`}
					</p>
				)}
				{endDate && endHours && daysAreTheSame && (
					<p className={styles.info('item-column-label')}>
						{`${t.until} ${endHours}`}
					</p>
				)}
				{endDate && !daysAreTheSame && (
					<p className={styles.info('item-column-label')}>
						{`${t.until} ${endDate}`}
						{endHours && ` ${t.at} ${endHours}`}
					</p>
				)}
			</>
		)
	} else {
		data = (
			<>
				{startDate && (
					<p
						className={styles.info('item-column-label')}
					>{`${t.on} ${startDate}`}</p>
				)}
				{hours && (
					<p
						className={styles.info('item-column-label')}
					>{`${t.at} ${hours}`}</p>
				)}
			</>
		)
	}

	return (
		<div className={styles.info('item')}>
			<span className={styles.info('item-icon')}>
				<Icon icon={icon} />
			</span>
			<div className={styles.info('item-column')}>{data}</div>
		</div>
	)
}
